import { ExecutionStep, StepType, TreeNode, TreeEdge, TreeMetadata, Vertex, Edge } from '../types/rust-engine-interfaces';
import { step, arrayPayload, stringPayload, treePayload, graphPayload, normalizeArray, genericFallback } from './fallback-helpers';

declare module '../types/rust-engine-interfaces' {
  interface TreeMetadata {
    rangeQuery?: [number, number];
    lazyIndex?: number;
    searchPrefix?: string;
    isTerminal?: boolean;
    parent?: number[];
    root?: number;
    compressedPath?: string[];
  }
  interface ArrayMetadata {
    lowbit?: number;
    queryIndex?: number;
    updateIndex?: number;
    level?: number;
    forwardEdges?: Array<{ from: number; to: number; level: number }>;
  }
  interface GraphMetadata {
    splitAxis?: 'x' | 'y';
    splitLine?: number;
    queryRect?: [number, number, number, number];
  }
}

export function dsFallback(id: string, raw: any): ExecutionStep[] {
  const lowered = id.toLowerCase();
  if (lowered.includes('union') || lowered.includes('disjoint')) return unionFind(raw);
  if (lowered.includes('fenwick')) return fenwickTree(raw);
  if (lowered.includes('segment') || lowered.includes('sqrt')) return segmentTree(raw);
  if (lowered.includes('trie')) return trie(raw);
  if (lowered.includes('skip')) return skipList(raw);
  if (lowered.includes('kd')) return kdTree(raw);
  if (lowered.includes('rope')) return rope(raw);
  if (lowered.includes('avl')) return avlTree(raw);
  if (lowered.includes('red-black') || lowered.includes('redblack')) return redBlackTree(raw);
  if (lowered.includes('splay')) return splayTree(raw);
  if (lowered.includes('treap')) return treapTree(raw);
  if (lowered.includes('cartesian')) return cartesianTree(raw);
  if (lowered.includes('binomial')) return binomialHeap(raw);
  if (lowered.includes('pairing')) return pairingHeap(raw);
  if (lowered.includes('leftist')) return leftistHeap(raw);
  if (lowered.includes('b-plus')) return bPlusTree(raw);
  if (lowered.includes('b-tree')) return bTree(raw);
  if (lowered.includes('heap')) return binaryHeap(raw);
  return unionFind(raw);
}

function unionFind(raw: any): ExecutionStep[] {
  const size = Math.min(raw?.size ?? 8, 12);
  const ops = raw?.operations || [{ type: 'union', params: [0, 1] }, { type: 'union', params: [2, 3] }, { type: 'union', params: [1, 3] }, { type: 'find', params: [0] }];
  const parent = Array.from({ length: size }, (_, i) => i);
  const steps: ExecutionStep[] = [];
  const toTree = (): { nodes: TreeNode[]; edges: TreeEdge[] } => {
    const nodes: TreeNode[] = parent.map((p, i) => ({ id: `uf-${i}`, value: i, parent: p === i ? undefined : `uf-${p}` }));
    const edges: TreeEdge[] = [];
    parent.forEach((p, i) => { if (p !== i) edges.push({ from: `uf-${p}`, to: `uf-${i}`, type: 'parent' }); });
    const children = new Map<string, string[]>();
    edges.forEach((e) => children.set(e.from, [...(children.get(e.from) ?? []), e.to]));
    nodes.forEach((n) => { const c = children.get(n.id); if (c) n.children = c; });
    return { nodes, edges };
  };
  const rootOf = (x: number): { root: number; path: number[] } => {
    const path = [x];
    while (parent[path[path.length - 1]!] !== path[path.length - 1]) path.push(parent[path[path.length - 1]!]!);
    return { root: path[path.length - 1]!, path };
  };
  const push = (type: StepType, current: number | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = toTree();
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode: current === undefined ? undefined : `uf-${current}`, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, { parent: [...parent] }, 'Union-Find: each item starts as a tree root.', 1);
  for (const op of ops) {
    const [a, b] = op.params;
    const fa = rootOf(a);
    push(StepType.Comparison, a, { parent: [...parent], root: fa.root, compressedPath: fa.path.map((v) => `uf-${v}`) }, `Find(${a}) follows path ${fa.path.join(' → ')} to root ${fa.root}.`, 2);
    for (const v of fa.path) parent[v] = fa.root;
    if (op.type === 'union' && b !== undefined) {
      const fb = rootOf(b);
      push(StepType.Comparison, b, { parent: [...parent], root: fb.root, compressedPath: fb.path.map((v) => `uf-${v}`) }, `Find(${b}) follows path ${fb.path.join(' → ')} to root ${fb.root}.`, 3);
      for (const v of fb.path) parent[v] = fb.root;
      if (fa.root !== fb.root) parent[fa.root] = fb.root;
      push(StepType.Update, fa.root, { parent: [...parent], root: fb.root, compressedPath: [`uf-${fa.root}`, `uf-${fb.root}`] }, `Union ${a}, ${b}: link root ${fa.root} under ${fb.root}.`, 4);
    }
  }
  push(StepType.Completion, undefined, { parent: [...parent] }, 'Union-Find operations complete.', 5);
  return steps;
}

function segmentTree(raw: any): ExecutionStep[] {
  const arr = normalizeArray(raw);
  const n = arr.length;
  if (n === 0) return genericFallback('segment-tree', raw, 'data structures');

  const tree: number[] = Array(2 * n).fill(0);
  for (let i = 0; i < n; i++) tree[n + i] = arr[i]!;

  function buildTreeNodes(): { nodes: TreeNode[]; edges: TreeEdge[] } {
    const nodeMap = new Map<string, TreeNode>();
    const edges: TreeEdge[] = [];
    for (let i = 1; i < 2 * n; i++) {
      nodeMap.set(`node-${i}`, { id: `node-${i}`, value: tree[i]! });
    }
    for (let i = 2; i < 2 * n; i++) {
      const parent = Math.floor(i / 2);
      const type: TreeEdge['type'] = i % 2 === 0 ? 'left' : 'right';
      const parentId = `node-${parent}`;
      const childId = `node-${i}`;
      edges.push({ from: parentId, to: childId, type });
      const parentNode = nodeMap.get(parentId)!;
      if (type === 'left') parentNode.left = childId;
      else parentNode.right = childId;
      const childNode = nodeMap.get(childId)!;
      childNode.parent = parentId;
    }
    return { nodes: Array.from(nodeMap.values()), edges };
  }

  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, treePayload(buildTreeNodes().nodes, buildTreeNodes().edges, { currentNode: 'node-1', visitedNodes: [] }), { line: 1, explanation: 'Segment Tree: initialize leaves.' }));

  const visited: string[] = [];
  for (let i = n - 1; i > 0; i--) {
    tree[i] = tree[2 * i]! + tree[2 * i + 1]!;
    const currentNode = `node-${i}`;
    visited.push(currentNode);
    const { nodes, edges } = buildTreeNodes();
    s.push(step(s.length, StepType.Update, treePayload(nodes, edges, { currentNode, visitedNodes: [...visited] }), { line: 3, explanation: `Internal node ${i} = ${tree[i]}.` }));
  }

  const ql = Math.max(0, Math.min(raw?.query?.[0] ?? 1, n - 1));
  const qr = Math.max(ql, Math.min(raw?.query?.[1] ?? Math.min(n - 1, 4), n - 1));
  let l = ql + n;
  let r = qr + n;
  const queryVisited: string[] = [];
  while (l <= r) {
    if (l % 2 === 1) queryVisited.push(`node-${l++}`);
    if (r % 2 === 0) queryVisited.push(`node-${r--}`);
    l = Math.floor(l / 2);
    r = Math.floor(r / 2);
    const snap = buildTreeNodes();
    s.push(step(s.length, StepType.Comparison, treePayload(snap.nodes, snap.edges, { rangeQuery: [ql, qr], lazyIndex: l, visitedNodes: [...queryVisited] }), { line: 6, explanation: `Range query [${ql}, ${qr}] visits ${queryVisited.join(', ') || 'ancestors'}.` }));
  }

  const { nodes, edges } = buildTreeNodes();
  s.push(step(s.length, StepType.Completion, treePayload(nodes, edges, { rangeQuery: [ql, qr], visitedNodes: [...visited, ...queryVisited] }), { line: 7, explanation: 'Segment Tree built and queried.' }));
  return s;
}

function trie(raw: any): ExecutionStep[] {
  const words: string[] = (raw?.words || ['cat', 'car', 'dog', 'door']).slice(0, 8);
  const target: string = raw?.target || 'car';

  const nodeSet = new Map<string, TreeNode>();
  const edgeList: TreeEdge[] = [];

  const terminal = new Set<string>();
  nodeSet.set('', { id: 'root', value: '*' });

  for (const word of words) {
    let prefix = '';
    for (let i = 0; i < word.length; i++) {
      const char = word[i]!;
      const parentPrefix = prefix;
      prefix += char;
      if (!nodeSet.has(prefix)) {
        const isEnd = i === word.length - 1;
        const nodeId = `node-${prefix}`;
        nodeSet.set(prefix, {
          id: nodeId,
          value: isEnd ? `${char}*` : char,
        });
        const parentId = parentPrefix === '' ? 'root' : `node-${parentPrefix}`;
        edgeList.push({ from: parentId, to: nodeId, type: 'left' });
      }
    }
    terminal.add(prefix);
    const endNode = nodeSet.get(prefix);
    if (endNode) endNode.value = `${String(endNode.value).replace('*', '')}*`;
  }

  const childMap = new Map<string, string[]>();
  edgeList.forEach((e) => childMap.set(e.from, [...(childMap.get(e.from) ?? []), e.to]));
  for (const n of nodeSet.values()) {
    const children = childMap.get(n.id);
    if (children) n.children = children;
  }

  const nodes = Array.from(nodeSet.values());

  nodes.forEach((n) => {
    const edge = edgeList.find(e => e.to === n.id);
    if (edge) (n as TreeNode).parent = edge.from;
  });

  const s: ExecutionStep[] = [];
  const chars = target.split('');

  s.push(step(s.length, StepType.Initialization, treePayload(nodes, edgeList, { currentNode: 'root', searchPrefix: '', isTerminal: false }), { line: 1, explanation: `Trie: ${words.length} words inserted. Searching for "${target}".` }));

  let currentPrefix = '';
  let found = true;
  const visitedNodes: string[] = ['root'];

  for (let i = 0; i < chars.length; i++) {
    const char = chars[i]!;
    const nextPrefix = currentPrefix + char;
    const nodeId = `node-${nextPrefix}`;
    const exists = nodeSet.has(nextPrefix);

    if (exists) {
      currentPrefix = nextPrefix;
      visitedNodes.push(nodeId);
      s.push(step(s.length, StepType.Comparison, treePayload(nodes, edgeList, { currentNode: nodeId, visitedNodes, searchPrefix: nextPrefix, isTerminal: terminal.has(nextPrefix) }), { line: 3, explanation: `Prefix "${nextPrefix}" exists — follow edge '${char}'.` }));
    } else {
      found = false;
      s.push(step(s.length, StepType.Decision, treePayload(nodes, edgeList, { currentNode: 'root', visitedNodes, searchPrefix: nextPrefix, isTerminal: false }), { line: 4, explanation: `Prefix "${nextPrefix}" not found — search fails at '${char}'.` }));
      break;
    }
  }

  const finalNode = found ? `node-${target}` : 'root';
  s.push(step(s.length, StepType.Completion, treePayload(nodes, edgeList, { currentNode: finalNode, visitedNodes, searchPrefix: target, isTerminal: terminal.has(target) }), { line: 6, explanation: `Search complete: "${target}" ${words.includes(target) ? 'found in trie' : 'not a complete word'}.` }));
  return s;
}

function fenwickTree(raw: any): ExecutionStep[] {
  const arr = normalizeArray(raw);
  const n = arr.length;
  const bit = Array(n + 1).fill(0) as number[];
  const steps: ExecutionStep[] = [];
  const payload = (meta: Parameters<typeof arrayPayload>[1]) => arrayPayload(bit.slice(1), meta);
  steps.push(step(steps.length, StepType.Initialization, payload({ highlightIndices: [] }), { line: 1, explanation: 'Fenwick Tree: initialize BIT array.' }));
  for (let i = 0; i < n; i++) {
    let idx = i + 1;
    while (idx <= n) {
      bit[idx] = bit[idx]! + arr[i]!;
      const lb = idx & -idx;
      steps.push(step(steps.length, StepType.Update, payload({ currentIndex: idx - 1, updateIndex: idx - 1, lowbit: lb, highlightIndices: [idx - 1] }), { line: 3, explanation: `Point update: add ${arr[i]} at BIT[${idx}], lowbit=${lb}, next ${idx + lb}.` }));
      idx += lb;
    }
  }
  let q = Math.min(raw?.queryIndex ?? Math.floor(n / 2), n - 1) + 1;
  while (q > 0) {
    const lb = q & -q;
    steps.push(step(steps.length, StepType.Comparison, payload({ currentIndex: q - 1, queryIndex: q - 1, lowbit: lb, highlightIndices: [q - 1] }), { line: 5, explanation: `Prefix sum visits BIT[${q}], lowbit=${lb}, previous ${q - lb}.` }));
    q -= lb;
  }
  steps.push(step(steps.length, StepType.Completion, payload({ highlightIndices: [] }), { line: 7, explanation: 'Fenwick Tree complete.' }));
  return steps;
}

function skipList(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw).slice().sort((a, b) => a - b);
  const target = raw?.target ?? vals[Math.floor(vals.length / 2)] ?? 0;
  const levels = Math.min(4, Math.max(2, Math.ceil(Math.log2(vals.length + 1))));
  const rows = Array.from({ length: levels }, (_, level) => vals.filter((_, i) => i % (2 ** (levels - level - 1)) === 0));
  const flat = rows.flat();
  const forwardEdges = rows.flatMap((row, level) => row.slice(0, -1).map((_, i) => ({ from: i, to: i + 1, level })));
  const steps: ExecutionStep[] = [];
  steps.push(step(steps.length, StepType.Initialization, arrayPayload(flat, { level: 0, forwardEdges, highlightIndices: [] }), { line: 1, explanation: `Skip List: ${levels} levels built.` }));
  const visited: number[] = [];
  for (let level = 0; level < rows.length; level++) {
    const row = rows[level]!;
    for (let i = 0; i < row.length && row[i]! <= target; i++) visited.push(flat.indexOf(row[i]!));
    steps.push(step(steps.length, StepType.Comparison, arrayPayload(flat, { level, forwardEdges, highlightIndices: [...new Set(visited)] }), { line: 3, explanation: `Search level ${level}: move forward while key <= ${target}.` }));
  }
  steps.push(step(steps.length, StepType.Update, arrayPayload(flat, { level: rows.length - 1, forwardEdges, highlightIndices: [...new Set(visited)] }), { line: 5, explanation: `Insert/search path for ${target} highlighted across levels.` }));
  steps.push(step(steps.length, StepType.Completion, arrayPayload(flat, { level: rows.length - 1, forwardEdges, highlightIndices: [...new Set(visited)] }), { line: 6, explanation: 'Skip List complete.' }));
  return steps;
}

function kdTree(raw: any): ExecutionStep[] {
  const base = Array.isArray(raw?.points) ? raw.points : [[30, 40], [5, 25], [70, 70], [10, 12], [50, 30], [35, 45]];
  const points: Array<{ id: string; x: number; y: number }> = base.slice(0, 10).map((p: any, i: number) => ({ id: `kd-${i}`, x: Number(p[0] ?? p.x ?? i * 10), y: Number(p[1] ?? p.y ?? i * 7) }));
  const vertices: Vertex[] = points.map((p: { id: string; x: number; y: number }) => ({ id: p.id, value: `${p.x},${p.y}`, label: `(${p.x},${p.y})`, x: p.x, y: p.y, position: { x: p.x, y: p.y } }));
  const edges: Edge[] = [];
  const steps: ExecutionStep[] = [];
  const queryRect: [number, number, number, number] = raw?.queryRect ?? [0, 0, 50, 50];
  const build = (ids: number[], depth: number, parent?: number) => {
    if (ids.length === 0) return;
    const axis = depth % 2 === 0 ? 'x' : 'y';
    ids.sort((a, b) => points[a]![axis] - points[b]![axis]);
    const mid = Math.floor(ids.length / 2);
    const cur = ids[mid]!;
    if (parent !== undefined) edges.push({ from: `kd-${parent}`, to: `kd-${cur}`, directed: true });
    steps.push(step(steps.length, StepType.Update, graphPayload(vertices, [...edges], { directed: true, currentVertex: `kd-${cur}`, splitAxis: axis, splitLine: points[cur]![axis], queryRect }), { line: 2, explanation: `KD-Tree split by ${axis}=${points[cur]![axis]}.` }));
    build(ids.slice(0, mid), depth + 1, cur);
    build(ids.slice(mid + 1), depth + 1, cur);
  };
  steps.push(step(steps.length, StepType.Initialization, graphPayload(vertices, [], { directed: true, queryRect }), { line: 1, explanation: 'KD-Tree: load 2D points.' }));
  build(points.map((_: { id: string; x: number; y: number }, i: number) => i), 0);
  const inside = points.filter((p: { id: string; x: number; y: number }) => p.x >= queryRect[0] && p.x <= queryRect[2] && p.y >= queryRect[1] && p.y <= queryRect[3]).map((p: { id: string; x: number; y: number }) => p.id);
  steps.push(step(steps.length, StepType.Completion, graphPayload(vertices, edges, { directed: true, visitedVertices: inside, queryRect }), { line: 6, explanation: `KD-Tree rectangle query found ${inside.length} points.` }));
  return steps;
}

function rope(raw: any): ExecutionStep[] {
  const text: string = raw?.text || 'HelloWorld'; const op: string = raw?.operation || 'insert'; const params: any[] = raw?.params || [5, 'Beautiful'];
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, stringPayload(text, { word1: text }), { line: 1, explanation: `Rope: initial string "${text}".` }));
  let result = text;
  if (op === 'insert') { result = text.slice(0, params[0] as number) + String(params[1]) + text.slice(params[0] as number); s.push(step(s.length, StepType.Update, stringPayload(result, { currentIndex: params[0] as number, word1: result }), { line: 3, explanation: `Insert "${String(params[1])}" at position ${params[0]}.` })); }
  else if (op === 'split') { const splitAt = params[0] as number; s.push(step(s.length, StepType.Comparison, stringPayload(text, { currentIndex: splitAt, word1: text }), { line: 3, explanation: `Split rope at position ${splitAt}.` })); result = text.slice(0, splitAt) + '|' + text.slice(splitAt); s.push(step(s.length, StepType.Update, stringPayload(result, { currentIndex: splitAt, word1: result }), { line: 4, explanation: `Split into "${text.slice(0, splitAt)}" | "${text.slice(splitAt)}".` })); }
  s.push(step(s.length, StepType.Completion, stringPayload(result, { word1: result }), { line: 6, explanation: `Rope operation complete: "${result}".` }));
  return s;
}

interface DSNode {
  id: string;
  key: number;
  left?: string;
  right?: string;
  children?: string[];
  parent?: string;
  h?: number;
  height?: number;
  color?: 'red' | 'black';
  priority?: number;
  degree?: number;
  npl?: number;
  isLeaf?: boolean;
  keys?: number[];
}

function nodeHeight(map: Map<string, DSNode>, id?: string): number {
  if (!id) return 0;
  return map.get(id)?.h ?? 0;
}

function balanceFactor(map: Map<string, DSNode>, id: string): number {
  const n = map.get(id);
  if (!n) return 0;
  return nodeHeight(map, n.left) - nodeHeight(map, n.right);
}

function updateHeight(map: Map<string, DSNode>, id: string): void {
  const n = map.get(id);
  if (!n) return;
  n.h = 1 + Math.max(nodeHeight(map, n.left), nodeHeight(map, n.right));
}

function prepareAVL(map: Map<string, DSNode>): void {
  for (const n of map.values()) {
    n.height = balanceFactor(map, n.id);
  }
}

function balanceFactors(map: Map<string, DSNode>): Record<string, number> {
  const bf: Record<string, number> = {};
  for (const id of map.keys()) bf[id] = balanceFactor(map, id);
  return bf;
}

function serializeTree(map: Map<string, DSNode>): { nodes: TreeNode[]; edges: TreeEdge[] } {
  const nodes: TreeNode[] = [];
  const edges: TreeEdge[] = [];
  for (const n of map.values()) {
    const node: TreeNode = {
      id: n.id,
      value: n.keys ? n.keys.join('|') : n.priority !== undefined ? `${n.key}:${n.priority}` : n.key,
      parent: n.parent,
      color: n.color,
      height: n.height,
    };
    if (n.children && n.children.length > 0) {
      node.children = n.children;
      for (const childId of n.children) edges.push({ from: n.id, to: childId, type: 'parent' });
    } else {
      node.left = n.left;
      node.right = n.right;
      if (n.left) edges.push({ from: n.id, to: n.left, type: 'left' });
      if (n.right) edges.push({ from: n.id, to: n.right, type: 'right' });
    }
    nodes.push(node);
  }
  return { nodes, edges };
}

function treeEdgesBetween(map: Map<string, DSNode>, ids: string[]): TreeEdge[] {
  const set = new Set(ids);
  const edges: TreeEdge[] = [];
  for (const id of ids) {
    const n = map.get(id);
    if (!n) continue;
    if (n.left && set.has(n.left)) edges.push({ from: id, to: n.left, type: 'left' });
    if (n.right && set.has(n.right)) edges.push({ from: id, to: n.right, type: 'right' });
  }
  return edges;
}

function rotateLeft(map: Map<string, DSNode>, rootId: string, xId: string): string {
  const x = map.get(xId)!;
  const y = map.get(x.right!)!;
  x.right = y.left;
  if (y.left) map.get(y.left)!.parent = xId;
  y.parent = x.parent;
  if (x.parent) {
    const p = map.get(x.parent)!;
    if (p.left === xId) p.left = y.id; else p.right = y.id;
  } else {
    rootId = y.id;
  }
  y.left = xId;
  x.parent = y.id;
  return rootId;
}

function rotateRight(map: Map<string, DSNode>, rootId: string, yId: string): string {
  const y = map.get(yId)!;
  const x = map.get(y.left!)!;
  y.left = x.right;
  if (x.right) map.get(x.right)!.parent = yId;
  x.parent = y.parent;
  if (y.parent) {
    const p = map.get(y.parent)!;
    if (p.left === yId) p.left = x.id; else p.right = x.id;
  } else {
    rootId = x.id;
  }
  x.right = yId;
  y.parent = x.id;
  return rootId;
}

function avlTree(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let rootId: string | undefined;
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `n${nextId++}`;
    const n: DSNode = { id, key, h: 1 };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    prepareAVL(map);
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'AVL Tree: initialized.', 1);
  for (const key of vals) {
    if (!rootId) {
      const n = newNode(key);
      rootId = n.id;
      push(StepType.Update, n.id, { key, visitedNodes: [n.id], balanceFactors: balanceFactors(map) }, `Insert ${key} as root.`, 2);
      continue;
    }
    const path: string[] = [];
    let parent = map.get(rootId)!;
    let dir: 'left' | 'right' = key < parent.key ? 'left' : 'right';
    path.push(parent.id);
    push(StepType.Comparison, parent.id, { key, visitedNodes: [...path], balanceFactors: balanceFactors(map) }, `Compare ${key} with ${parent.key}.`, 3);
    while (parent[dir]) {
      parent = map.get(parent[dir]!)!;
      dir = key < parent.key ? 'left' : 'right';
      path.push(parent.id);
      push(StepType.Comparison, parent.id, { key, visitedNodes: [...path], balanceFactors: balanceFactors(map) }, `Compare ${key} with ${parent.key}.`, 3);
    }
    const n = newNode(key);
    n.parent = parent.id;
    parent[dir] = n.id;
    path.push(n.id);
    push(StepType.Update, n.id, { key, visitedNodes: [...path], balanceFactors: balanceFactors(map) }, `Insert ${key} as ${dir} child of ${parent.key}.`, 4);
    for (let i = path.length - 1; i >= 0; i--) {
      const id = path[i]!;
      updateHeight(map, id);
      const bf = balanceFactor(map, id);
      if (Math.abs(bf) > 1) {
        const n = map.get(id)!;
        const leftHeavy = bf > 1;
        const childId = leftHeavy ? n.left! : n.right!;
        const childBF = balanceFactor(map, childId);
        let rotation: 'LL' | 'RR' | 'LR' | 'RL';
        if (leftHeavy) rotation = childBF >= 0 ? 'LL' : 'LR';
        else rotation = childBF <= 0 ? 'RR' : 'RL';
        const rotatedIds = [n.id, childId];
        push(StepType.Comparison, n.id, { key, visitedNodes: rotatedIds, rotation, highlightedEdges: treeEdgesBetween(map, rotatedIds), balanceFactors: balanceFactors(map) }, `${n.key} is unbalanced (${bf}); ${rotation} rotation.`, 6);
        if (rotation === 'LL') rootId = rotateRight(map, rootId!, id);
        else if (rotation === 'RR') rootId = rotateLeft(map, rootId!, id);
        else if (rotation === 'LR') {
          rootId = rotateLeft(map, rootId!, childId);
          rootId = rotateRight(map, rootId, id);
        } else {
          rootId = rotateRight(map, rootId!, childId);
          rootId = rotateLeft(map, rootId, id);
        }
        updateHeight(map, id);
        updateHeight(map, childId);
        const grandchildId = rotation === 'LR' ? map.get(childId)!.right : rotation === 'RL' ? map.get(childId)!.left : undefined;
        if (grandchildId) updateHeight(map, grandchildId);
        push(StepType.Update, n.id, { key, visitedNodes: rotatedIds, rotation, highlightedEdges: treeEdgesBetween(map, rotatedIds), balanceFactors: balanceFactors(map) }, `${rotation} rotation completed at ${n.key}.`, 7);
        break;
      } else {
        push(StepType.Comparison, id, { key, visitedNodes: [...path], balanceFactors: balanceFactors(map) }, `${map.get(id)!.key} balance factor ${bf}.`, 5);
      }
    }
  }
  push(StepType.Completion, rootId, { visitedNodes: Array.from(map.keys()), balanceFactors: balanceFactors(map) }, `AVL Tree complete with ${map.size} nodes.`, 8);
  return steps;
}

function redBlackTree(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let rootId: string | undefined;
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `n${nextId++}`;
    const n: DSNode = { id, key, color: 'red' };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Red-Black Tree: initialized.', 1);
  for (const key of vals) {
    const z = newNode(key);
    let parentId: string | undefined;
    let current = rootId;
    const path: string[] = [];
    while (current) {
      const c = map.get(current)!;
      path.push(current);
      push(StepType.Comparison, current, { key, visitedNodes: [...path] }, `Compare ${key} with ${c.key}.`, 2);
      parentId = current;
      current = key < c.key ? c.left : c.right;
    }
    z.parent = parentId;
    if (!parentId) rootId = z.id;
    else {
      const p = map.get(parentId)!;
      if (key < p.key) p.left = z.id; else p.right = z.id;
    }
    path.push(z.id);
    push(StepType.Update, z.id, { key, visitedNodes: [...path], color: 'red' }, `Insert ${key} as red node.`, 3);
    let fixId = z.id;
    while (fixId !== rootId && map.get(map.get(fixId)!.parent!)!.color === 'red') {
      const zNode = map.get(fixId)!;
      const pNode = map.get(zNode.parent!)!;
      const gNode = map.get(pNode.parent!)!;
      const pIsLeft = gNode.left === pNode.id;
      const yId = pIsLeft ? gNode.right : gNode.left;
      const y = yId ? map.get(yId) : undefined;
      if (y?.color === 'red') {
        pNode.color = 'black';
        y.color = 'black';
        gNode.color = 'red';
        push(StepType.StateChange, pNode.id, { key, visitedNodes: [zNode.id, pNode.id, y.id, gNode.id], color: 'black' }, `Recolor parent ${pNode.key} and uncle ${y.key} black.`, 4);
        push(StepType.StateChange, gNode.id, { key, visitedNodes: [zNode.id, pNode.id, y.id, gNode.id], color: 'red' }, `Recolor grandparent ${gNode.key} red.`, 4);
        fixId = gNode.id;
      } else {
        if (pIsLeft && zNode.id === pNode.right) {
          fixId = pNode.id;
          rootId = rotateLeft(map, rootId!, fixId);
          push(StepType.Update, fixId, { key, visitedNodes: [zNode.id, pNode.id, gNode.id] }, `Left rotate ${pNode.key} (zig-zag).`, 5);
        } else if (!pIsLeft && zNode.id === pNode.left) {
          fixId = pNode.id;
          rootId = rotateRight(map, rootId!, fixId);
          push(StepType.Update, fixId, { key, visitedNodes: [zNode.id, pNode.id, gNode.id] }, `Right rotate ${pNode.key} (zig-zag).`, 5);
        }
        const zNode2 = map.get(fixId)!;
        const pNode2 = map.get(zNode2.parent!)!;
        const gNode2 = map.get(pNode2.parent!)!;
        pNode2.color = 'black';
        gNode2.color = 'red';
        push(StepType.StateChange, pNode2.id, { key, visitedNodes: [zNode2.id, pNode2.id, gNode2.id], color: 'black' }, `Recolor ${pNode2.key} black.`, 6);
        push(StepType.StateChange, gNode2.id, { key, visitedNodes: [zNode2.id, pNode2.id, gNode2.id], color: 'red' }, `Recolor ${gNode2.key} red.`, 6);
        if (pNode2.id === gNode2.left) {
          rootId = rotateRight(map, rootId!, gNode2.id);
          push(StepType.Update, gNode2.id, { key, visitedNodes: [zNode2.id, pNode2.id, gNode2.id] }, `Right rotate ${gNode2.key}.`, 7);
        } else {
          rootId = rotateLeft(map, rootId!, gNode2.id);
          push(StepType.Update, gNode2.id, { key, visitedNodes: [zNode2.id, pNode2.id, gNode2.id] }, `Left rotate ${gNode2.key}.`, 7);
        }
      }
    }
    const root = map.get(rootId!)!;
    if (root.color !== 'black') {
      root.color = 'black';
      push(StepType.StateChange, root.id, { visitedNodes: [root.id], color: 'black' }, 'Recolor root black.', 8);
    }
  }
  push(StepType.Completion, rootId, { visitedNodes: Array.from(map.keys()) }, 'Red-Black Tree complete.', 9);
  return steps;
}

function splayTree(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let rootId: string | undefined;
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `n${nextId++}`;
    const n: DSNode = { id, key };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Splay Tree: initialized.', 1);
  for (const key of vals) {
    const z = newNode(key);
    let parentId: string | undefined;
    let current = rootId;
    const path: string[] = [];
    while (current) {
      const c = map.get(current)!;
      path.push(current);
      push(StepType.Comparison, current, { key, visitedNodes: [...path] }, `Compare ${key} with ${c.key}.`, 2);
      parentId = current;
      current = key < c.key ? c.left : c.right;
    }
    if (!parentId) rootId = z.id;
    else {
      const p = map.get(parentId)!;
      z.parent = parentId;
      if (key < p.key) p.left = z.id; else p.right = z.id;
      path.push(parentId);
    }
    push(StepType.Update, z.id, { key, visitedNodes: [...path, z.id] }, `Insert ${key}.`, 3);
    let splayId = z.id;
    while (map.get(splayId)?.parent) {
      const sNode = map.get(splayId)!;
      const pNode = map.get(sNode.parent!)!;
      const gNode = pNode.parent ? map.get(pNode.parent) : undefined;
      const sIsLeft = pNode.left === splayId;
      const pIsLeft = gNode ? gNode.left === pNode.id : undefined;
      let splayStep: 'zig' | 'zig-zig' | 'zig-zag';
      if (!gNode) splayStep = 'zig';
      else splayStep = sIsLeft === pIsLeft ? 'zig-zig' : 'zig-zag';
      const highlightedIds = [sNode.id, pNode.id, ...(gNode ? [gNode.id] : [])];
      const highlightedEdges = treeEdgesBetween(map, highlightedIds);
      push(StepType.Comparison, splayId, { key, visitedNodes: highlightedIds, splayStep, highlightedEdges }, `Splay ${sNode.key}: ${splayStep}.`, 4);
      if (splayStep === 'zig') {
        if (sIsLeft) rootId = rotateRight(map, rootId!, pNode.id);
        else rootId = rotateLeft(map, rootId!, pNode.id);
      } else if (splayStep === 'zig-zig') {
        if (sIsLeft) {
          rootId = rotateRight(map, rootId!, gNode!.id);
          rootId = rotateRight(map, rootId, pNode.id);
        } else {
          rootId = rotateLeft(map, rootId!, gNode!.id);
          rootId = rotateLeft(map, rootId, pNode.id);
        }
      } else {
        if (sIsLeft) {
          rootId = rotateRight(map, rootId!, pNode.id);
          rootId = rotateLeft(map, rootId, gNode!.id);
        } else {
          rootId = rotateLeft(map, rootId!, pNode.id);
          rootId = rotateRight(map, rootId, gNode!.id);
        }
      }
      push(StepType.Update, splayId, { key, visitedNodes: highlightedIds, splayStep, highlightedEdges: treeEdgesBetween(map, highlightedIds) }, `Splay ${splayStep} applied.`, 5);
    }
  }
  push(StepType.Completion, rootId, { visitedNodes: Array.from(map.keys()) }, 'Splay Tree complete.', 6);
  return steps;
}

function treapTree(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let rootId: string | undefined;
  let nextId = 1;
  let prng = 1;
  const nextPriority = () => {
    prng = (prng * 9301 + 49297) % 233280;
    return Math.floor((prng / 233280) * 99) + 1;
  };
  const newNode = (key: number): DSNode => {
    const id = `n${nextId++}`;
    const n: DSNode = { id, key, priority: nextPriority() };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Treap: initialized.', 1);
  for (const key of vals) {
    const n = newNode(key);
    if (!rootId) {
      rootId = n.id;
      push(StepType.Update, n.id, { key, priority: n.priority, visitedNodes: [n.id] }, `Insert root ${key}:${n.priority}.`, 2);
      continue;
    }
    const path: string[] = [];
    let parent = map.get(rootId)!;
    let dir: 'left' | 'right' = key < parent.key ? 'left' : 'right';
    path.push(parent.id);
    push(StepType.Comparison, parent.id, { key, priority: n.priority, visitedNodes: [...path] }, `Compare ${key} with ${parent.key}.`, 3);
    while (parent[dir]) {
      parent = map.get(parent[dir]!)!;
      dir = key < parent.key ? 'left' : 'right';
      path.push(parent.id);
      push(StepType.Comparison, parent.id, { key, priority: n.priority, visitedNodes: [...path] }, `Compare ${key} with ${parent.key}.`, 3);
    }
    n.parent = parent.id;
    parent[dir] = n.id;
    path.push(n.id);
    push(StepType.Update, n.id, { key, priority: n.priority, visitedNodes: [...path] }, `Insert ${key}:${n.priority}.`, 4);
    let curId = n.id;
    while (true) {
      const cur = map.get(curId)!;
      if (!cur.parent) break;
      const par = map.get(cur.parent)!;
      if (cur.priority! >= par.priority!) break;
      const curIsLeft = par.left === curId;
      const edge: TreeEdge = { from: par.id, to: cur.id, type: curIsLeft ? 'left' : 'right' };
      push(StepType.Comparison, cur.id, { key, priority: cur.priority, visitedNodes: [...path], highlightedEdges: [edge] }, `Priority violation: ${cur.key}:${cur.priority} < ${par.key}:${par.priority}.`, 5);
      if (curIsLeft) rootId = rotateRight(map, rootId!, par.id);
      else rootId = rotateLeft(map, rootId!, par.id);
      push(StepType.Update, cur.id, { key, priority: cur.priority, visitedNodes: [...path], highlightedEdges: [{ from: cur.id, to: par.id, type: curIsLeft ? 'right' : 'left' }] }, `Rotate ${cur.key} up by priority.`, 6);
      curId = cur.id;
    }
  }
  push(StepType.Completion, rootId, { visitedNodes: Array.from(map.keys()) }, 'Treap complete.', 7);
  return steps;
}

function cartesianTree(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let rootId: string | undefined;
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `n${nextId++}`;
    const n: DSNode = { id, key };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Cartesian Tree: initialized.', 1);
  const stack: DSNode[] = [];
  for (const key of vals) {
    const n = newNode(key);
    let lastPopped: DSNode | undefined;
    while (stack.length && stack[stack.length - 1]!.key > key) {
      lastPopped = stack.pop();
    }
    if (lastPopped) {
      n.left = lastPopped.id;
      lastPopped.parent = n.id;
    }
    if (stack.length) {
      const parent = stack[stack.length - 1]!;
      parent.right = n.id;
      n.parent = parent.id;
    } else {
      rootId = n.id;
    }
    stack.push(n);
    const highlightedEdges: TreeEdge[] = [];
    if (n.parent) {
      const p = map.get(n.parent)!;
      highlightedEdges.push({ from: n.parent, to: n.id, type: p.left === n.id ? 'left' : 'right' });
    }
    if (n.left) highlightedEdges.push({ from: n.id, to: n.left, type: 'left' });
    push(StepType.Update, n.id, { key, visitedNodes: stack.map((x) => x.id), highlightedEdges }, `Cartesian node ${key}: parent is nearest smaller element, root is minimum.`, 2);
  }
  push(StepType.Completion, rootId, { visitedNodes: Array.from(map.keys()) }, 'Cartesian Tree complete.', 3);
  return steps;
}

function heapMap(heap: DSNode[]): Map<string, DSNode> {
  const map = new Map<string, DSNode>();
  for (let i = 0; i < heap.length; i++) {
    const n = heap[i]!;
    n.left = 2 * i + 1 < heap.length ? heap[2 * i + 1]!.id : undefined;
    n.right = 2 * i + 2 < heap.length ? heap[2 * i + 2]!.id : undefined;
    n.parent = i > 0 ? heap[Math.floor((i - 1) / 2)]!.id : undefined;
    map.set(n.id, n);
  }
  return map;
}

function binaryHeap(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const heap: DSNode[] = [];
  let nextId = 1;
  const newNode = (key: number): DSNode => ({ id: `h${nextId++}`, key });
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(heapMap(heap));
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Binary Heap: initialized.', 1);
  for (const key of vals) {
    const n = newNode(key);
    heap.push(n);
    push(StepType.Update, n.id, { heapAction: 'sift-up', visitedNodes: [n.id] }, `Insert ${key}.`, 2);
    let i = heap.length - 1;
    while (i > 0) {
      const p = Math.floor((i - 1) / 2);
      if (heap[i]!.key <= heap[p]!.key) break;
      const swappedIds = [heap[i]!.id, heap[p]!.id];
      const tmp = heap[i]!;
      heap[i] = heap[p]!;
      heap[p] = tmp;
      push(StepType.Update, heap[i]!.id, { heapAction: 'sift-up', swappedNodes: swappedIds, visitedNodes: [heap[i]!.id, heap[p]!.id] }, `Sift up: swap ${heap[i]!.key} with ${heap[p]!.key}.`, 3);
      i = p;
    }
  }
  while (heap.length > 1) {
    const maxNode = heap[0]!;
    const last = heap.pop()!;
    heap[0] = last;
    push(StepType.Update, last.id, { heapAction: 'sift-down', swappedNodes: [maxNode.id, last.id], visitedNodes: [maxNode.id, last.id] }, `Extract ${maxNode.key}: replace root with ${last.key}.`, 4);
    let i = 0;
    while (true) {
      const l = 2 * i + 1;
      const r = 2 * i + 2;
      let largest = i;
      if (l < heap.length && heap[l]!.key > heap[largest]!.key) largest = l;
      if (r < heap.length && heap[r]!.key > heap[largest]!.key) largest = r;
      if (largest === i) break;
      const swappedIds = [heap[i]!.id, heap[largest]!.id];
      const tmp = heap[i]!;
      heap[i] = heap[largest]!;
      heap[largest] = tmp;
      push(StepType.Update, heap[i]!.id, { heapAction: 'sift-down', swappedNodes: swappedIds, visitedNodes: [heap[i]!.id, heap[largest]!.id] }, `Sift down: swap ${heap[i]!.key} with ${heap[largest]!.key}.`, 5);
      i = largest;
    }
  }
  push(StepType.Completion, heap[0]?.id, { heapAction: 'sift-down', visitedNodes: heap[0] ? [heap[0].id] : [] }, 'Binary Heap complete.', 6);
  return steps;
}

function binomialHeap(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  const heapRoot: DSNode = { id: 'heap-root', key: 0, children: [] };
  map.set(heapRoot.id, heapRoot);
  let roots: DSNode[] = [];
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `b${nextId++}`;
    const n: DSNode = { id, key, degree: 0, children: [] };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    heapRoot.children = roots.map((r) => r.id);
    roots.forEach((r) => { r.parent = heapRoot.id; });
    roots.sort((a, b) => (a.degree ?? 0) - (b.degree ?? 0));
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Binomial Heap: initialized.', 1);
  for (const key of vals) {
    let n = newNode(key);
    roots.push(n);
    push(StepType.Update, n.id, { heapAction: 'merge', degree: 0, visitedNodes: roots.map((r) => r.id) }, `Insert ${key} as new B0 tree.`, 2);
    while (true) {
      const same = roots.filter((r) => (r.degree ?? 0) === (n.degree ?? 0) && r.id !== n.id);
      if (same.length === 0) break;
      const other = same[0]!;
      const [parent, child] = n.key <= other.key ? [n, other] : [other, n];
      parent.children!.push(child.id);
      parent.degree = (parent.degree ?? 0) + 1;
      child.parent = parent.id;
      roots = roots.filter((r) => r.id !== child.id);
      push(StepType.Update, parent.id, { heapAction: 'link', degree: parent.degree, visitedNodes: [parent.id, child.id] }, `Link ${child.key} under ${parent.key}: degree ${parent.degree}.`, 3);
      n = parent;
    }
  }
  push(StepType.Completion, roots[0]?.id, { visitedNodes: roots.map((r) => r.id) }, 'Binomial Heap complete.', 4);
  return steps;
}

function pairingHeap(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let root: DSNode | undefined;
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `p${nextId++}`;
    const n: DSNode = { id, key, children: [] };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Pairing Heap: initialized.', 1);
  for (const key of vals) {
    const n = newNode(key);
    if (!root) {
      root = n;
      push(StepType.Update, n.id, { heapAction: 'merge-pairs', visitedNodes: [n.id] }, `Insert root ${key}.`, 2);
      continue;
    }
    if (n.key < root.key) {
      n.children!.push(root.id);
      root.parent = n.id;
      root = n;
    } else {
      root.children!.push(n.id);
      n.parent = root.id;
    }
    push(StepType.Update, root.id, { heapAction: 'merge-pairs', visitedNodes: [root.id, n.id] }, `Insert ${key} and meld with root.`, 2);
  }
  if (root && root.children && root.children.length > 0) {
    const oldRoot = root;
    const children = root.children;
    for (const childId of children) {
      const c = map.get(childId)!;
      c.parent = undefined;
    }
    root.children = [];
    let newRoots = children.map((id) => map.get(id)!);
    while (newRoots.length > 1) {
      const merged: DSNode[] = [];
      for (let i = 0; i < newRoots.length; i += 2) {
        if (i + 1 === newRoots.length) { merged.push(newRoots[i]!); break; }
        const a = newRoots[i]!;
        const b = newRoots[i + 1]!;
        const [parent, child] = a.key < b.key ? [a, b] : [b, a];
        parent.children!.push(child.id);
        child.parent = parent.id;
        parent.parent = undefined;
        merged.push(parent);
        push(StepType.Update, parent.id, { heapAction: 'merge-pairs', visitedNodes: [a.id, b.id] }, `Merge pair ${a.key} and ${b.key}: ${parent.key} becomes parent.`, 3);
      }
      newRoots = merged;
    }
    root = newRoots[0];
    if (oldRoot.id !== root!.id) map.delete(oldRoot.id);
  }
  if (root && root.children && root.children.length > 0) {
    const targetId = root.children[0]!;
    const target = map.get(targetId)!;
    target.key = Math.min(...vals) - 1;
    push(StepType.Update, target.id, { heapAction: 'decrease-key', visitedNodes: [root.id, target.id] }, `Decrease key of ${target.id} to ${target.key}.`, 4);
    if (target.key < root.key) {
      root.children = root.children.filter((id) => id !== target.id);
      target.children!.push(root.id);
      target.parent = undefined;
      root.parent = target.id;
      root = target;
    }
    push(StepType.Update, root.id, { heapAction: 'decrease-key', visitedNodes: [root.id, target.id] }, `Meld decreased node with root.`, 5);
  }
  push(StepType.Completion, root?.id, { visitedNodes: root ? [root.id, ...(root.children ?? [])] : [] }, 'Pairing Heap complete.', 6);
  return steps;
}

function leftistHeap(raw: any): ExecutionStep[] {
  const vals = normalizeArray(raw?.values ?? raw);
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let root: DSNode | undefined;
  let nextId = 1;
  const newNode = (key: number): DSNode => {
    const id = `l${nextId++}`;
    const n: DSNode = { id, key, npl: 0, left: undefined, right: undefined };
    map.set(id, n);
    return n;
  };
  const nplOf = (id?: string): number => (id ? map.get(id)?.npl ?? 0 : 0);
  const mergeLeftist = (a: DSNode | undefined, b: DSNode | undefined): DSNode | undefined => {
    if (!a) return b;
    if (!b) return a;
    if (a.key > b.key) { const t = a; a = b; b = t; }
    const rightNode = a.right ? map.get(a.right) : undefined;
    const mergedRight = mergeLeftist(rightNode, b);
    a.right = mergedRight?.id;
    if (mergedRight) mergedRight.parent = a.id;
    const nplL = nplOf(a.left);
    const nplR = nplOf(a.right);
    if (nplL < nplR) { const t = a.left; a.left = a.right; a.right = t; }
    a.npl = 1 + nplR;
    return a;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    for (const n of map.values()) n.height = n.npl;
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };

  push(StepType.Initialization, undefined, {}, 'Leftist Heap: initialized.', 1);
  for (const key of vals) {
    const n = newNode(key);
    root = mergeLeftist(root, n);
    if (root) root.parent = undefined;
    push(StepType.Update, root?.id, { heapAction: 'merge', npl: root?.npl, visitedNodes: root ? [root.id] : [] }, `Merge ${key} into leftist heap.`, 2);
  }
  push(StepType.Completion, root?.id, { heapAction: 'merge', npl: root?.npl, visitedNodes: root ? [root.id] : [] }, 'Leftist Heap complete.', 3);
  return steps;
}

function bTreeFromValues(vals: number[], isBPlus: boolean): ExecutionStep[] {
  const steps: ExecutionStep[] = [];
  const map = new Map<string, DSNode>();
  let rootId: string | undefined;
  let nextId = 1;
  const T = 2;
  const newNode = (keys: number[], isLeaf: boolean, children: string[]): DSNode => {
    const id = `bt${nextId++}`;
    const n: DSNode = { id, key: keys[0] ?? 0, keys: [...keys], isLeaf, children: [...children] };
    map.set(id, n);
    return n;
  };
  const push = (type: StepType, currentNode: string | undefined, meta: TreeMetadata, explanation: string, line: number) => {
    const { nodes, edges } = serializeTree(map);
    steps.push(step(steps.length, type, treePayload(nodes, edges, { currentNode, ...meta }), { line, explanation }));
  };
  const updateChildrenParent = (n: DSNode) => {
    for (const cid of n.children ?? []) map.get(cid)!.parent = n.id;
  };
  const splitChild = (parentId: string, idx: number) => {
    const parent = map.get(parentId)!;
    const childId = parent.children![idx]!;
    const child = map.get(childId)!;
    const mid = Math.floor(child.keys!.length / 2);
    const midKey = child.keys![mid]!;
    const rightNode = newNode(child.keys!.slice(mid + 1), child.isLeaf!, child.isLeaf ? [] : child.children!.slice(mid + 1));
    child.keys = child.keys!.slice(0, mid);
    if (!child.isLeaf) child.children = child.children!.slice(0, mid + 1);
    updateChildrenParent(child);
    updateChildrenParent(rightNode);
    parent.keys!.splice(idx, 0, midKey);
    parent.children!.splice(idx + 1, 0, rightNode.id);
    push(StepType.Update, parent.id, { isLeaf: parent.isLeaf, keys: parent.keys, splitIndex: idx, range: [parent.keys![0]!, parent.keys![parent.keys!.length - 1]!] }, `Split child at ${idx}: median ${midKey} moved up.`, 3);
  };
  const insertNonFull = (nodeId: string, key: number) => {
    const node = map.get(nodeId)!;
    let i = node.keys!.length - 1;
    if (node.isLeaf) {
      while (i >= 0 && key < node.keys![i]!) i--;
      node.keys!.splice(i + 1, 0, key);
      node.key = node.keys![0]!;
      push(StepType.Update, node.id, { key, isLeaf: true, keys: node.keys, range: [node.keys![0]!, node.keys![node.keys!.length - 1]!] }, `Insert ${key} into leaf.`, 4);
    } else {
      while (i >= 0 && key < node.keys![i]!) i--;
      let childIdx = i + 1;
      const childId = node.children![childIdx]!;
      if (map.get(childId)!.keys!.length === 2 * T - 1) {
        splitChild(node.id, childIdx);
        if (key > node.keys![childIdx]!) childIdx++;
      }
      insertNonFull(node.children![childIdx]!, key);
    }
  };
  const insert = (key: number) => {
    if (!rootId) {
      const root = newNode([key], true, []);
      rootId = root.id;
      push(StepType.Update, root.id, { key, isLeaf: true, keys: root.keys, range: [key, key] }, `Insert ${key} as root leaf.`, 2);
      return;
    }
    const root = map.get(rootId)!;
    if (root.keys!.length === 2 * T - 1) {
      const newRoot = newNode([], false, [rootId]);
      rootId = newRoot.id;
      root.parent = newRoot.id;
      updateChildrenParent(newRoot);
      splitChild(newRoot.id, 0);
      insertNonFull(rootId, key);
    } else {
      insertNonFull(rootId, key);
    }
  };

  push(StepType.Initialization, undefined, {}, `${isBPlus ? 'B+ Tree' : 'B-tree'}: initialized.`, 1);
  for (const key of vals) insert(key);
  const root = map.get(rootId!)!;
  push(StepType.Completion, rootId, { isLeaf: root.isLeaf, keys: root.keys, range: [root.keys![0]!, root.keys![root.keys!.length - 1]!] }, `${isBPlus ? 'B+ Tree' : 'B-tree'} complete.`, 5);
  return steps;
}

function bTree(raw: any): ExecutionStep[] {
  return bTreeFromValues(normalizeArray(raw?.values ?? raw), false);
}

function bPlusTree(raw: any): ExecutionStep[] {
  return bTreeFromValues(normalizeArray(raw?.values ?? raw), true);
}

